/* eslint-disable*/
import { Box, Typography, useTheme } from '@material-ui/core';
import {
  performanceSeoData,
  performanceSeoSetting
} from 'feactures/performancereview/performancereviewSlice';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { useSelector } from 'react-redux';

const SeoCompareLineGraph = ({ compareType }) => {
  const [rotation, setRotation] = useState(0);
  const dialogRef = useRef(null); // ダイアログの幅を取得するための ref
  const setting = useSelector(performanceSeoSetting);
  const seodata = useSelector(performanceSeoData);
  const seoType = setting.seoType;

  useEffect(() => {
    const updateRotation = () => {
      if (dialogRef.current) {
        const dialogWidth = dialogRef.current.offsetWidth; // ダイアログの幅を取得
        setRotation(dialogWidth < 900 ? 45 : 0); // 幅900px以下なら斜め、それ以上なら横向き
      }
    };

    window.addEventListener('resize', updateRotation);
    updateRotation(); // 初回実行

    return () => window.removeEventListener('resize', updateRotation);
  }, []);

  const formattedYokoziku = seodata.yokoziku.map((date, index) => {
    const [month, day] = date.split('/'); // 'MM/DD' の形式に分解
    const monthName = `${parseInt(month, 10)}`;
    const dayName = `${parseInt(day, 10)}`;
    return `${monthName}/${dayName}`;
  });

  // 1日おきのラベルを作成
  const formattedDates = seodata.yokoziku.map((date, index) => {
    const [month, day] = date.split('/'); // 'MM/DD' の形式に分解
    const monthName = `${parseInt(month, 10)}`;
    const dayName = `${parseInt(day, 10)}`;

    return index % 2 === 0 ? `${monthName}/${dayName}` : ''; // ✅ 奇数日のみ表示
  });

  const theme = useTheme();
  let label = '';
  let color = '';
  let seo = '';
  let rank = 0;
  if (compareType == 'own') {
    label = '自社';
    color = '#ACABE3';
    seo = seoType == 'organic' ? 'オーガニック' : '広告';
    rank =
      seoType == 'organic' ? seodata.own.organicRank : seodata.own.sponsorRank;
  } else if (compareType == 'other') {
    label = '競合';
    color = '#4CAF50';
    seo = seoType == 'organic' ? 'オーガニック' : '広告';
    rank =
      seoType == 'organic'
        ? seodata.other.organicRank
        : seodata.other.sponsorRank;
  }

  const generateDateListTodayStart = (dataLength) => {
    const today = new Date();
    const startDate = new Date();
    startDate.setDate(today.getDate() - dataLength + 1); // 今日から過去へ

    const dateList = [];
    for (let i = 0; i < dataLength; i++) {
      const currentDate = new Date(startDate);
      currentDate.setDate(startDate.getDate() + i);
      const month = currentDate.getMonth() + 1;
      const day = currentDate.getDate();
      dateList.push(`${month}/${day}`);
    }

    return dateList;
  };

  /**
   * 文字列から決定的な数値シードを生成する関数
   */
  const createSeed = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = (hash << 5) - hash + str.charCodeAt(i);
      hash |= 0;
    }
    return hash;
  };

  /**
   * シードに基づいて決定的な乱数（0〜1）を生成する関数
   */
  const seededRandom = (seed) => {
    return function () {
      let t = (seed += 0x6d2b79f5);
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  };

  /**
   * 【全要件対応・最終版】連続10日で補完を打ち切る決定的補完関数
   * @param {number[]} data - ランキングデータの配列
   * @param {string[]} dateList - 日付文字列の配列 ('MM/DD'形式)
   * @param {number} max - 圏外とみなす順位の閾値
   * @param {number} fluctuationWidth - 通常時の順位の変動幅（例: 3なら-3〜+3）
   * @param {number} maxConsecutiveDays - 補完を許容する最大連続日数（この日数に達したら圏外）
   * @returns {number[]} 補完後のランキングデータの配列
   */
  const complementRankWithLimit = (
    data,
    dateList,
    max,
    fluctuationWidth = 3,
    maxConsecutiveDays = 10 // 上限10日を引数で指定
  ) => {
    const isInvalid = (val) =>
      val === -99 ||
      val === 0 ||
      val === null ||
      val === undefined ||
      val > max;

    const today = new Date();
    const todayStart = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    const isFuture = (dateStr) => {
      if (!dateStr) return true;
      const [month, day] = dateStr.split('/').map(Number);
      const date = new Date(today.getFullYear(), month - 1, day);
      return date > todayStart;
    };

    const result = [...data];
    let lastValidRank = null;
    let consecutiveInvalidCount = 0; // ★連続圏外カウンター

    for (let i = 0; i < result.length; i++) {
      const currentRank = result[i];
      const currentDateStr = dateList[i];

      if (isFuture(currentDateStr)) continue;

      if (!isInvalid(currentRank)) {
        // 有効な値が見つかった場合
        lastValidRank = currentRank;
        consecutiveInvalidCount = 0; // ★カウンターをリセット
      } else {
        // 無効値の場合
        consecutiveInvalidCount++; // ★カウンターをインクリメント

        // ★★★ 打ち切りルールの判定 ★★★
        if (
          lastValidRank !== null &&
          consecutiveInvalidCount < maxConsecutiveDays
        ) {
          // カウンターが上限未満なら補完処理を行う
          const seed = createSeed(currentDateStr + lastValidRank.toString());
          const random = seededRandom(seed)();
          let supplementedRank;

          if (lastValidRank === 1) {
            // 1位の時の処理
            const dropWidth = 1 + Math.floor(random * fluctuationWidth);
            supplementedRank = lastValidRank + dropWidth;
          } else {
            // 2位以降の処理
            const fluctuation =
              Math.floor(random * (fluctuationWidth * 2 + 1)) -
              fluctuationWidth;
            supplementedRank = lastValidRank + fluctuation;
            if (supplementedRank < 1) supplementedRank = 1;
          }

          // 閾値チェック（順位がmaxを超えたら補完を停止）
          if (supplementedRank > max) {
            lastValidRank = null; // 補完の連鎖を断ち切る
            // result[i] は元の無効値のまま
          } else {
            result[i] = supplementedRank;
            // ★重要：補完した値を次の計算元にする
            lastValidRank = supplementedRank;
          }
        } else {
          // カウンターが上限に達した場合、または補完元の有効な順位がない場合
          // 何もせず、result[i] は元の無効値のまま（＝圏外扱い）
          // lastValidRank を null にして、これ以降の補完を停止する
          lastValidRank = null;
        }
      }
    }
    return result;
  };

  const generateChartData = (label, seo, color, dataset, max) => {
    return {
      datasets: [
        {
          label: label + seo,
          data: dataset?.map((x) => {
            if (x === -99 || x > max)
              return seo.includes('オーガニック') ? max + 40 : max + 5; // 圏外：オーガニック最大値+50 、広告最大値 + 5
            if (x === 0) return null; // データなし：０
            return Number(x);
          }),
          backgroundColor: color,
          borderColor: color,
          pointRadius: 3,
          pointStyle: 'circle',
          pointBorderColor: color,
          pointBackgroundColor: color,
          lineTension: 0.2
        }
      ],
      labels: formattedDates
    };
  };

  const dataOrganic = useMemo(() => {
    const rawData = seodata[compareType]?.organic || [];
    const dateList =
      seodata.yokoziku || generateDateListTodayStart(rawData.length);

    const complemented = complementRankWithLimit(rawData, dateList, 240);

    return generateChartData(label, seo, color, complemented, 240);
  }, [seodata, seodata.yokoziku, compareType, label, color, seo]);

  const dataSponsor = useMemo(() => {
    const rawData = seodata[compareType]?.sponsor || [];
    const dateList =
      seodata.yokoziku || generateDateListTodayStart(rawData.length);
    const complemented = complementRankWithLimit(rawData, dateList, 20);

    return generateChartData(label, seo, color, complemented, 20);
  }, [seodata, seodata.yokoziku, compareType, label, color, seo]);

  const generateChartOptions = (min, max, label, stepSize) => ({
    scales: {
      x: {
        ticks: {
          autoSkip: false,
          maxRotation: rotation,
          minRotation: rotation,
          padding: 10,
          font: {
            size: '10px'
          }
        },
        grid: { display: false, drawBorder: false },
        title: { display: false }
      },
      y: {
        suggestedMin: 1,
        max: stepSize == 40 ? 290 : 25,
        reverse: true,
        ticks: {
          maxTicksLimit: 20,
          stepSize: stepSize,
          fontColor: theme.palette.text.secondary,
          font: {
            family: "'M PLUS 1p', sans-serif"
          },
          callback: function (value) {
            if (value === 1) return '1位';
            if (stepSize == 40 && value == 280) return '圏外';
            if (stepSize == 5 && value == 25) return '圏外';
            return value > 0 ? `${value}位` : ''; // 整数でない場合は表示しない
          }
        },
        afterBuildTicks: function (scale) {
          // 圏外と1位のデフォルト設定
          let newTicks = [
            { label: '圏外', value: stepSize == 40 ? 280 : 25 }, // 圏外を追加
            ...scale.ticks.slice(1, -1), // 既存の ticks を保持
            { label: '1位', value: 1 } // 1位を設定
          ];

          scale.ticks = newTicks;
        },
        grid: {
          display: true,
          borderDash: [2],
          drawBorder: false
        }
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          title: (tooltipItems) => {
            // X軸の省略されたラベルではなく、元データ `seodata.yokoziku` から正しい日付を取得
            const index = tooltipItems[0].dataIndex;
            return formattedYokoziku[index]; // ✅ すべての日付をツールチップに表示
          },
          label: (tooltipItem) => {
            let value = tooltipItem.raw;
            if (value > max) return `${tooltipItem.dataset.label}: 圏外`;
            if (value === 0) return `${tooltipItem.dataset.label}: データ無し`;
            return `${tooltipItem.dataset.label}: ${value}位`;
          }
        }
      },
      legend: { position: 'bottom' },
      datalabels: {
        display: false
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    cornerRadius: 20,
    layout: {
      padding: {
        top: 30
      }
    }
  });

  const optionsOrganic = generateChartOptions(
    1,
    240,
    `${label}オーガニック`,
    40
  );
  const optionsSponsor = generateChartOptions(1, 20, `${label}広告`, 5);

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={1}
        minHeight="3rem"
      >
        <Typography fontWeight="bold">
          {compareType == 'own' && setting.ownKeyword}
          {compareType == 'other' && setting.otherKeyword}
        </Typography>
      </Box>

      <Box
        display="flex"
        width="100%"
        height="100%"
        maxHeight="88%"
        flexDirection="row"
        paddingX="1rem"
        ref={dialogRef}
      >
        <Box display="flex" flexDirection="column" width="100%" flexGrow={1}>
          <Box flexGrow={1} height="100%">
            <Line
              data={seoType == 'organic' ? dataOrganic : dataSponsor}
              options={seoType == 'organic' ? optionsOrganic : optionsSponsor}
            />
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SeoCompareLineGraph;
